import { Voucher } from '@gongxigames/framework/integrations/pariplay/Voucher';
import { PPConfiguration } from '@gongxigames/framework/integrations/pariplay/PPConfiguration';
import { IGameSessionPromotionInterface, PromotionBalanceEntryQualifier, PromotionTypes } from '@gongxigames/framework/promotions';

const ERROR_MESSAGE_DOES_NOT_HAVE_VOUCHER = 'Voucher is not defined';
const VOUCHER_KEYS_FOR_UPDATE = ['RoundsLeft', 'RoundsTotal', 'WinAmount', 'RoundsUsed'] as const;

export type GameSessionPromotionInitData = {
    voucher: Voucher;
    next_action: string;
};

export class GameSessionPromotion implements IGameSessionPromotionInterface {
    private voucher?: Voucher;

    public init({ voucher, next_action }: GameSessionPromotionInitData): void {
        this.voucher = voucher;

        if (this.voucher && next_action !== 'spin') {
            this.subtractPromotion();
        }
    }

    public update(newVoucher: Voucher): void {
        if (this.hasPromotion()) {
            const voucher = this.getVoucher();

            VOUCHER_KEYS_FOR_UPDATE.forEach((key) => {
                voucher[key] = newVoucher[key];
            });
        }
    }

    public getQualifier(): PromotionBalanceEntryQualifier {
        return 'free-round';
    }

    public getType(): PromotionTypes {
        return PromotionTypes.GameSessionPromotion;
    }

    public hasPromotion(): boolean {
        return this.voucher !== undefined;
    }

    public getBet(): number {
        return PPConfiguration.convertDoubleToInt(this.getVoucher().DefaultStake);
    }

    public getID(): string {
        return this.getVoucher().VoucherId.toString();
    }

    public getPlayedRounds(): number {
        let playedRounds: number = this.getVoucher().RoundsUsed;

        if (playedRounds === undefined) {
            playedRounds = this.getTotalRounds() - this.getLeftRounds();
        }

        return playedRounds;
    }

    public getLeftRounds(): number {
        return this.getVoucher().RoundsLeft;
    }

    public getRemainedRounds(): number {
        return this.getTotalRounds() - this.getPlayedRounds();
    }

    public getTotalRounds(): number {
        return this.getVoucher().RoundsTotal;
    }

    public getWin(): number {
        return PPConfiguration.convertDoubleToInt(this.getVoucher().WinAmount);
    }

    public isActive(): boolean {
        return this.voucher !== undefined;
    }

    public isCurrentInUse(): boolean {
        return this.isActive();
    }

    public isCurrentExpired(): boolean {
        return false;
    }

    public shouldShowContinuePopup(): boolean {
        if (this.voucher !== undefined) {
            const { RoundsLeft, RoundsTotal } = this.voucher;

            return RoundsLeft !== RoundsTotal;
        }

        return false;
    }

    public subtractPromotion(): void {
        if (this.getVoucher().RoundsUsed !== undefined) {
            this.getVoucher().RoundsUsed += 1;
        }

        this.getVoucher().RoundsLeft -= 1;
    }

    public getAdditionalFieldsForServerRequest(): Record<string, any> | undefined {
        return undefined;
    }

    private getVoucher(): Voucher {
        if (!this.voucher) {
            throw new Error(ERROR_MESSAGE_DOES_NOT_HAVE_VOUCHER);
        }

        return this.voucher;
    }
}
